import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { FileText, Plus, Pencil, Trash2, ArrowRightCircle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const STATI = ['Inviato', 'In trattativa', 'Accettato', 'Rifiutato'];
const STATO_COLORS = { Inviato: 'bg-blue-100 text-blue-800', 'In trattativa': 'bg-yellow-100 text-yellow-800', Accettato: 'bg-emerald-100 text-emerald-800', Rifiutato: 'bg-red-100 text-red-800' };
const fmtEur = (v) => `€ ${(v || 0).toLocaleString('it-IT')}`;
const empty = { cliente_id: '', oggetto: '', data_invio: '', importo_parcella: 0, probabilita: 0.5, descrizione: '', note: '' };

export default function StudioPreventiviPage() {
  const [items, setItems] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [p, c] = await Promise.all([studioApi.getPreventivi(), studioApi.getClienti()]);
      setItems(p.data); setClienti(c.data);
    } catch { toast.error('Errore'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditingId(p.id); setOpen(true); };
  const save = async () => {
    if (!form.cliente_id || !form.oggetto) { toast.error('Cliente e oggetto obbligatori'); return; }
    const payload = { ...form, importo_parcella: parseFloat(form.importo_parcella) || 0, probabilita: parseFloat(form.probabilita) || 0 };
    try {
      if (editingId) { await studioApi.updatePreventivo(editingId, payload); toast.success('Aggiornato'); }
      else { await studioApi.createPreventivo(payload); toast.success('Creato'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Eliminare preventivo "${p.codice || p.oggetto}"?`)) return;
    try { await studioApi.deletePreventivo(p.id); toast.success('Eliminato'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const convertToCommessa = async (p) => {
    if (!window.confirm(`Convertire il preventivo "${p.codice}" in commessa attiva?`)) return;
    try { await studioApi.convertPreventivo(p.id); toast.success('Convertito in commessa'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const downloadPdf = async (p) => {
    try {
      const r = await studioApi.downloadPreventivoPdf(p.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Preventivo_${p.codice}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF preventivo scaricato (con timbro e firma)');
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Errore generazione PDF');
      toast.error(msg);
    }
  };

  const totVal = items.reduce((s, p) => s + (p.valore_ponderato || 0), 0);

  return (
    <div className="space-y-4 p-4 lg:p-6" data-testid="studio-preventivi-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2"><FileText className="w-6 h-6 text-indigo-600" /><h1 className="text-2xl font-bold">Preventivi</h1><Badge>{items.length}</Badge></div>
          <p className="text-sm text-gray-600 mt-1">Valore ponderato pipeline: <strong className="text-indigo-700">{fmtEur(totVal)}</strong></p>
        </div>
        <Button onClick={openCreate} data-testid="new-preventivo-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo preventivo</Button>
      </motion.div>

      {loading ? <p className="text-gray-500 italic">Caricamento...</p> : (
        <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase"><tr>
              <th className="text-left p-2.5">Codice</th><th className="text-left p-2.5">Cliente</th><th className="text-left p-2.5">Oggetto</th>
              <th className="text-left p-2.5">Data invio</th><th className="text-right p-2.5">Parcella</th><th className="text-center p-2.5">Prob.</th>
              <th className="text-right p-2.5">Valore pond.</th><th className="text-left p-2.5">Stato</th><th></th>
            </tr></thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} className="border-t hover:bg-gray-50">
                  <td className="p-2.5 font-mono text-xs text-indigo-700">{p.codice}</td>
                  <td className="p-2.5">{p.cliente_nome || '-'}</td>
                  <td className="p-2.5 max-w-xs truncate">{p.oggetto}</td>
                  <td className="p-2.5 text-xs">{p.data_invio || '-'}</td>
                  <td className="p-2.5 text-right font-mono">{fmtEur(p.importo_parcella)}</td>
                  <td className="p-2.5 text-center">{Math.round((p.probabilita || 0) * 100)}%</td>
                  <td className="p-2.5 text-right font-mono font-semibold text-indigo-700">{fmtEur(p.valore_ponderato)}</td>
                  <td className="p-2.5"><Badge className={`text-xs ${STATO_COLORS[p.stato] || ''}`}>{p.stato}</Badge></td>
                  <td className="p-2.5 text-right">
                    <Button size="icon" variant="ghost" title="Scarica PDF preventivo (con timbro/firma)" onClick={() => downloadPdf(p)} data-testid={`download-preventivo-pdf-${p.id}`}><Download className="w-3.5 h-3.5 text-red-600" /></Button>
                    {p.stato !== 'Accettato' && <Button size="icon" variant="ghost" title="Converti in commessa" onClick={() => convertToCommessa(p)}><ArrowRightCircle className="w-4 h-4 text-emerald-600" /></Button>}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="9" className="text-center text-gray-500 italic py-10">Nessun preventivo.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica preventivo' : 'Nuovo preventivo'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Cliente *</Label>
              <Select value={form.cliente_id || '__none__'} onValueChange={(v) => setForm({ ...form, cliente_id: v === '__none__' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">--</SelectItem>{clienti.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Oggetto *</Label><Input value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} /></div>
            <div><Label>Data invio</Label><Input type="date" value={form.data_invio || ''} onChange={(e) => setForm({ ...form, data_invio: e.target.value })} /></div>
            <div><Label>Importo parcella (€)</Label><Input type="number" value={form.importo_parcella} onChange={(e) => setForm({ ...form, importo_parcella: e.target.value })} /></div>
            <div><Label>Probabilità (0-1)</Label><Input type="number" min="0" max="1" step="0.1" value={form.probabilita} onChange={(e) => setForm({ ...form, probabilita: e.target.value })} /></div>
            {editingId && (
              <div><Label>Stato</Label>
                <Select value={form.stato || 'Inviato'} onValueChange={(v) => setForm({ ...form, stato: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="md:col-span-2"><Label>Descrizione</Label><Textarea rows={3} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
